import flatten from 'lodash/flatten';
import uniq from 'lodash/uniq';

export interface IVertical {
  readonly top: number;
  readonly height: number;
}

export interface IHorizontal {
  readonly left: number;
  readonly width: number;
}

export interface IRect extends IVertical, IHorizontal {}

export interface IColorRect extends IRect {
  readonly colors: readonly string[];
}

/**
 * Return distance between two ranges, the result will be less tha
 * `0` if there's an overlap
 */
export function distance(aPos: number, aSize: number, bPos: number, bSize: number) {
  const min = Math.max(aPos, bPos);
  const max = Math.min(aPos + aSize, bPos + bSize);

  return min - max;
}

/**
 * Returns how many px between a and b overlap
 */
export function verticalOverlap(a: IVertical, b: IVertical) {
  // Height of the overlapping region
  return Math.max(0, -distance(a.top, a.height, b.top, b.height));
}

export function horizontalDistance(a: IHorizontal, b: IHorizontal) {
  return Math.max(0, distance(a.left, a.width, b.left, b.width));
}

/**
 * How much of either rect's vertical space should overlap to be
 * considered on the same line
 */
const SAME_LINE_MINIMUM_VERTICAL_OVERLAP = 0.7;

/**
 * How close two rects need to be next to each other to be considered
 * being on the same line
 */
const SAME_LINE_MAXIMUM_HORIZONTAL_DISTANCE = 20;

export function onSameLine(a: IRect, b: IRect): boolean {
  if (horizontalDistance(a, b) > SAME_LINE_MAXIMUM_HORIZONTAL_DISTANCE) {
    return false;
  }

  const overlap = verticalOverlap(a, b);

  return (
    overlap >= (a.height * SAME_LINE_MINIMUM_VERTICAL_OVERLAP) ||
    overlap >= (b.height * SAME_LINE_MINIMUM_VERTICAL_OVERLAP)
  );
}

/**
 * Sort first by line, then by horizontal position on the line, then
 * by length.  Allow rects to have slightly different heights. If
 * you can draw a horizontal line going through two rects, they're
 * considered to be on the same line.
 */
export function compareRects(a: IRect, b: IRect): number {
  if (!onSameLine(a, b)) {
    // Compare middle points of both rects
    return (a.top + a.height / 2) - (b.top + b.height / 2);
  }
  // Sort by left position
  const left = a.left - b.left;
  if (left !== 0) return left;
  // If they begin at the same x, sort by length
  return a.width - b.width;
}

function groupLines<RectType extends IRect>(rects: Iterable<RectType>): RectType[][] {
  const sorted = Array.from(rects).sort(compareRects);
  if (sorted.length === 0) {
    return [];
  }

  const lines = [[sorted[0]]];

  for (let i = 1; i < sorted.length; ++i) {
    const rect = sorted[i];
    if (onSameLine(rect, sorted[i - 1])) {
      lines[lines.length - 1].push(rect);
    } else {
      lines.push([rect]);
    }
  }

  return lines;
}

/**
 * Merges rects on the same line. Sorts lines vertically.
 *
 * Maximum shift vertical between rects is determined by
 * `onSameLine`, rects don't need to be touching in horizontal
 * direction.
 */
export function mergeLines(rects: Iterable<IRect>): IRect[] {
  return groupLines(rects).map((line) => {
    let top = Infinity;
    let bottom = -Infinity;
    let left = Infinity;
    let right = -Infinity;

    for (const rect of line) {
      if (rect.top < top) { top = rect.top; }
      if (rect.left < left) { left = rect.left; }

      const newBottom = rect.top + rect.height;
      if (newBottom > bottom) { bottom = newBottom; }

      const newRight = rect.left + rect.width;
      if (newRight > right) { right = newRight; }
    }

    return {
      top,
      left,
      width: right - left,
      height: bottom - top,
    };
  });
}

/**
 * Takes in rects of different colors. Parts that are overlapping
 * are replaced with rects that combine colors of intersecting
 * rects.
 */
export function splitOverlappingRects(rects: Iterable<IColorRect>): IColorRect[] {
  const result: IColorRect[] = [];

  groupLines(rects).forEach((line) => {
    const top = line[0].top;
    const height = line[0].height;

    // Points at which a rect either starts or ends
    const points = uniq(
      flatten(line.map(({ left, width }) => [left, left + width])),
    ).sort((a, b) => a - b);

    for (let i = 0; i < points.length - 1; ++i) {
      const left = points[i];
      const right = points[i + 1];

      result.push({
        top,
        height,
        left,
        width: right - left,
        colors: flatten(
          line
          .filter((rect) => rect.left < right && (rect.left + rect.width) > left)
          .map(({ colors }) => colors),
        // arbitrary sort just to keep all colors in all rects in the same order
        ).sort(),
      });
    }
  });

  return result;
}
